import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Skeleton,
  Badge,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { RxCross1 } from "react-icons/rx";
import { BsDownload } from "react-icons/bs";
import { NavLink, useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import creditmanagementService from "../../api/userService";
import userService from "../../api/userService";
import { interceptorsSetup } from "../../api/intreceptor";
import useFetchConfigSetting from "../../hooks/useConfigSetting";
import { resolveMediaUrl } from "../../utils";

interface TransactionRow {
  id: string;
  image_id: string | null;
  image_name: string | null;
  image_url: string | null;
  service_type: string;
  transaction_amount: number;
  balance: number;
  transaction_type: number;
  transaction_on: string;
  // Start time. Tag/Description/Title deductions: when their queue job was
  // requested (transaction_on is when it completed and was charged); null if
  // the job can't be found (image hard-deleted). Rows with no queue job
  // (credit adjustments, searches, uploads) start when they were recorded.
  requested_on?: string | null;
  // Images are soft-deleted: the transaction keeps its image (name + thumbnail
  // still resolve) and these say it was deleted afterwards. Absent/false for
  // active images and for rows with no image.
  image_deleted?: boolean;
  image_deleted_at?: string | null;
}

// One (image, IST day) bucket - the unit the table and the Excel export both
// show as a single row block. Rows with no image are bucketed per day too,
// but credit adjustments (Credit Assigned / Revoked / Recurring Credit
// Top-up) never share a bucket with usage deductions (Images Searched, Image
// Uploaded, or an old Tag/Description deduction whose image was hard-deleted
// before images became soft-deleted - those lost their image_id for good).
interface TransactionGroup {
  key: string;
  image_name: string;
  image_url: string | null;
  has_image: boolean;
  image_deleted: boolean;
  image_deleted_at: string | null;
  date: string;
  latest: number;
  entries: TransactionRow[];
}

// Every entry line in a group's stacked cells is this tall, so Date / Time /
// Service Type / Amount / Balance line up row for row.
const ENTRY_LINE_HEIGHT = 24;

const formatDateIST = (isoString: string) =>
  new Date(isoString).toLocaleDateString("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

// hourCycle h23 (not hour12:false) - the latter renders midnight as "24:xx".
const formatTimeIST = (isoString: string) =>
  new Date(isoString).toLocaleTimeString("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }) + " IST";

// A job can wait in the queue across midnight, so the requested date is shown
// too when it differs from the (completed) row date.
const formatRequestedIST = (row: TransactionRow) => {
  if (!row.requested_on) return "-";
  const requestedDate = formatDateIST(row.requested_on);
  const requestedTime = formatTimeIST(row.requested_on);
  return requestedDate === formatDateIST(row.transaction_on) ? requestedTime : `${requestedDate} ${requestedTime}`;
};

// Every credit-adjustment service_type contains "Credit"; no usage one does
// (usage is "Image Uploaded", "Images Searched" or "<mode> Generated").
const isCreditAdjustment = (row: TransactionRow) => /credit/i.test(row.service_type);

const groupTransactions = (rows: TransactionRow[]): TransactionGroup[] => {
  const groups = new Map<string, TransactionGroup>();
  rows.forEach((row) => {
    const date = formatDateIST(row.transaction_on);
    // Group on image_id, not image_name - two uploads can share a filename.
    const bucket = row.image_id ?? (isCreditAdjustment(row) ? "credit" : "usage");
    const key = `${bucket}|${date}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        image_name: row.image_name || "-",
        image_url: row.image_url,
        has_image: row.image_id != null,
        // Every row of a group shares one image_id, so the first row decides.
        image_deleted: !!row.image_deleted,
        image_deleted_at: row.image_deleted_at ?? null,
        date,
        latest: 0,
        entries: [],
      };
      groups.set(key, group);
    }
    group.entries.push(row);
    group.latest = Math.max(group.latest, new Date(row.transaction_on).getTime());
  });
  const result = Array.from(groups.values());
  result.forEach((group) =>
    group.entries.sort(
      (a, b) => new Date(a.transaction_on).getTime() - new Date(b.transaction_on).getTime()
    )
  );
  return result;
};

// The processed images are ~700 KB each; embedding them as-is made the
// workbook gigabytes large. A small JPEG is plenty for a ~36px Excel cell.
const THUMB_PX = 96;
const THUMB_DISPLAY_PX = 36;
const THUMB_FETCH_CONCURRENCY = 6;

const buildThumbnail = async (url: string) => {
  // cache "reload": the table's <img> tags already put these URLs in the
  // browser cache, and that cached copy was fetched without an Origin header
  // (no CORS headers, no Vary: Origin). Reusing it for this cross-origin
  // fetch() fails the CORS check, so bypass the cache read.
  const res = await fetch(url, { cache: "reload" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const bitmap = await createImageBitmap(await res.blob());
  try {
    const scale = Math.min(1, THUMB_PX / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // JPEG has no alpha - paint white first so transparent PNGs don't go black.
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/jpeg", 0.8)
    );
    return { buffer: await blob.arrayBuffer(), width, height };
  } finally {
    bitmap.close();
  }
};

const Transaction = () => {
  const [transactionData, setTransactionData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [transactionType, setTransactionType] = useState("All");
  const [filteredData, setFilteredData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ done: 0, total: 0 });
  const groupedData = useMemo(() => groupTransactions(filteredData), [filteredData]);
  const {fetchConfigSetting} = useFetchConfigSetting()
  const navigate = useNavigate();
  const toast = useToast()
  const comp_id = localStorage.getItem("comp_id")
  const storedRole = localStorage.getItem("role")
  const currentDate = new Date();
  const defaultToDate = currentDate.toISOString().slice(0, 10);

  useEffect(() => {
    interceptorsSetup(navigate);
  }, [navigate]);

  const firstDateOfMonth = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Adding leading zero if needed
    const day = '01'; // First day of the month

    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    const defaultDate = firstDateOfMonth();

    setFromDate(defaultDate);
    setToDate(defaultToDate);

    setRole(storedRole);
    if (storedRole === "User") {
      const storedEmail = localStorage.getItem("email");
      setEmail(storedEmail);
    } else if (storedRole === "superAdmin" || storedRole === "Admin") {
      getActiveUsers()
    }
    fetchConfigSetting()
  }, []);

  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    const defaultStartDate = firstDateOfMonth()
    const trans_info = {comp_id: comp_id, email: storedEmail, from_date: defaultStartDate, to_date: defaultToDate };
    if(trans_info.email.length >0 && trans_info.from_date.length > 0 && trans_info.to_date.length > 0){
       getTransaction(trans_info)
    }
  }, [])


	useEffect(() => {
		const storedEmail = localStorage.getItem("email");
		const defaultStartDate = firstDateOfMonth()

		if (storedRole === 'Admin') {
			const filterUser = users.find((user) => user.email === storedEmail);

			if (filterUser) {
				setEmail(filterUser.email)

				const trans_info = { comp_id: comp_id, email: filterUser.email, from_date: defaultStartDate, to_date: defaultToDate };
				if (trans_info.email.length > 0 && trans_info.from_date.length > 0 && trans_info.to_date.length > 0) {
					getTransaction(trans_info)
				}
			}
		}

		if (storedRole === 'superAdmin' && comp_id !== "" ) {
			const defaultUser = users[0]?.email
			console.log("de user:", defaultUser)
				setEmail(defaultUser)
				const trans_info = { comp_id: comp_id, email: defaultUser, 	from_date: defaultStartDate, to_date: defaultToDate };
				
				if (
					defaultUser && defaultUser.length > 0 && trans_info.from_date && trans_info.from_date.length > 0 && trans_info.to_date && trans_info.to_date.length > 0	) {
					getTransaction(trans_info);
				}
				
		}
	}, [storedRole, users])



  useEffect(() => {
    if (transactionType === "Credit") {
      let creditData = transactionData.filter(
        (transaction) => transaction.transaction_type === 1
      );
      setFilteredData(creditData);
    } else if (transactionType === "Debit") {
      let debitData = transactionData.filter(
        (transaction) => transaction.transaction_type === 2
      );
      setFilteredData(debitData);
    } else {
      setFilteredData(transactionData);
    }
  }, [transactionType]);

  const getActiveUsers = async () => {
     try{
      setIsLoading(true);
      const response = await userService.getActiveUsers(comp_id);
      setUsers(response)
     }catch(err){
      toast({
        title: `${err.response.data.detail}`,
        position: "top",
        status: "error",
        duration: 3000,
        variant: "left-accent",
        isClosable: true,
      })
      console.log("err:", err)
     }finally{
      setIsLoading(false)
     }
  };

  const handleResetTransaction = async() => {
	setFromDate(firstDateOfMonth());
	setToDate(defaultToDate);
	setTransactionType("All");
	setTransactionData([]);
	const storedEmail = localStorage.getItem("email");
	setEmail(storedEmail);
	
	const defaultStartDate = firstDateOfMonth()
	if (storedRole === 'Admin') {
		const filterUser = users.find((user) => user.email === storedEmail);

		if (filterUser) {
			setEmail(filterUser.email)

			const trans_info_Admin = { comp_id: comp_id, email: filterUser.email, from_date: defaultStartDate, to_date: defaultToDate };
			if (trans_info_Admin.email.length > 0 && trans_info_Admin.from_date.length > 0 && trans_info_Admin.to_date.length > 0) {
				getTransaction(trans_info_Admin)
			}
		}
	}
	if(storedRole === 'User') {
		const trans_info = { comp_id: comp_id, email: storedEmail, from_date: firstDateOfMonth(), to_date: defaultToDate }
		getTransaction(trans_info)
	}
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    await fetchTransactionData();
  };

  const fetchTransactionData = async () => {
    const trans_info = { comp_id,email, from_date: fromDate, to_date: toDate };
    try {
      setIsLoading(true);
      const response = await creditmanagementService.transactionData(trans_info);
      if (response.status === 200) {
        let data = response.data.transaction_data;
        setTransactionData(data);
        setFilteredData(data);
      }
    } catch (err) {
      console.log("fetch transaction err:", err);
      toast({
        title: `${err.response.data.detail}`,
        position: "top",
        status: "error",
        duration: 3000,
        variant: "left-accent",
        isClosable: true,
      })
    } finally {
      setIsLoading(false);
    }
  };

  const getTransaction= async (trans_info) => {
    try {
      setIsLoading(true);
      const response = await creditmanagementService.transactionData(
        trans_info
      );
      if (response.status === 200) {
        let data = response.data.transaction_data;
        setTransactionData(data);
        setFilteredData(data);
      }
    } catch (err) {
      console.log("fetch transaction err:", err);
      toast({
        title: `${err.response.data.detail}`,
        position: "top",
        status: "error",
        duration: 3000,
        variant: "left-accent",
        isClosable: true,
      })
    } finally {
      setIsLoading(false);
    }
  };

  // One stacked cell per column: a line per entry in the (image, day) group.
  const stackedCell = (group: TransactionGroup, render: (entry: TransactionRow) => React.ReactNode) => (
    <div style={{ width: "100%" }}>
      {group.entries.map((entry) => (
        <div key={entry.id} style={{ height: ENTRY_LINE_HEIGHT, lineHeight: `${ENTRY_LINE_HEIGHT}px` }}>
          {render(entry)}
        </div>
      ))}
    </div>
  );

  const columns = [
    {
      name: "Image Name",
      selector: (group: TransactionGroup) => group.image_name,
      cell: (group: TransactionGroup) => (
        <div>
          <div>{group.image_name}</div>
          {group.image_deleted && (
            <Tooltip
              hasArrow
              label={
                group.image_deleted_at
                  ? `Deleted on ${formatDateIST(group.image_deleted_at)}. The usage below was recorded before it was deleted.`
                  : "This image has been deleted."
              }
            >
              <Badge colorScheme="red" fontSize="10px">
                Deleted
              </Badge>
            </Tooltip>
          )}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Image",
      cell: (group: TransactionGroup) =>
        group.image_url ? (
          <img
            src={resolveMediaUrl(group.image_url)}
            alt={group.image_name}
            loading="lazy"
            style={{
              width: "50px",
              height: "100px",
              objectFit: "cover",
              borderRadius: "4px",
              // Deleted images keep their thumbnail (the files are kept) but look inactive.
              opacity: group.image_deleted ? 0.45 : 1,
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      id: "date",
      name: "Date",
      selector: (group: TransactionGroup) => group.latest,
      cell: (group: TransactionGroup) => stackedCell(group, () => group.date),
      sortable: true,
    },
    {
      name: "Start Time",
      cell: (group: TransactionGroup) => stackedCell(group, formatRequestedIST),
    },
    {
      name: "Complete Time",
      cell: (group: TransactionGroup) => stackedCell(group, (entry) => formatTimeIST(entry.transaction_on)),
    },
    {
      name: "Service Type",
      cell: (group: TransactionGroup) => stackedCell(group, (entry) => entry.service_type),
    },
    {
      name: "Transaction Amount",
      cell: (group: TransactionGroup) => stackedCell(group, (entry) => entry.transaction_amount.toFixed(1)),
    },
    {
      name: "Balance",
      cell: (group: TransactionGroup) => stackedCell(group, (entry) => entry.balance.toFixed(1)),
    },
  ];

  const tableStyles = {
    head: {
      style: {
        fontWeight: "bold",
        fontSize: "14px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#FFFFFF",
      },
    },
    cells: {
      style: {
        fontSize: "12px",
        // A group can span many lines - keep its image/name pinned to the top.
        alignItems: "flex-start",
        paddingTop: "8px",
        paddingBottom: "8px",
      },
    },
  };

  const handleExportExcel = async () => {
    if (!(filteredData.length > 0)) {
      return toast({
        title: `No data to export !`,
        position: "top",
        status: "warning",
        duration: 3000,
        variant: "left-accent",
        isClosable: true,
      });
    }
    setIsExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Utilization");
      worksheet.columns = [
        { header: "Image Name", key: "image_name", width: 28 },
        { header: "Image", key: "image", width: 10 },
        { header: "Image Status", key: "image_status", width: 24 },
        { header: "Date", key: "date", width: 14 },
        { header: "Start Time", key: "requested_time", width: 16 },
        { header: "Complete Time", key: "time", width: 16 },
        { header: "Service Type", key: "service_type", width: 22 },
        { header: "Transaction Amount", key: "transaction_amount", width: 18 },
        { header: "Balance", key: "balance", width: 14 },
      ];
      worksheet.getRow(1).font = { bold: true };

      // Each distinct image is downloaded, shrunk and embedded once, then
      // reused by every group that shows it (the same image recurs across
      // days) - not once per transaction row like before.
      const imageUrls = Array.from(
        new Set(groupedData.map((group) => group.image_url).filter(Boolean))
      ) as string[];
      const embeddedImages = new Map<string, { imageId: number; width: number; height: number }>();
      let failedThumbnails = 0;
      let nextImage = 0;
      setExportProgress({ done: 0, total: imageUrls.length });

      const imageWorker = async () => {
        while (nextImage < imageUrls.length) {
          const imageUrl = imageUrls[nextImage++];
          // One retry: a single fetch dropping (e.g. backend hot-reloading
          // mid-export) shouldn't cost an image its thumbnail.
          let thumb = null;
          for (let attempt = 0; attempt < 2 && !thumb; attempt++) {
            try {
              thumb = await buildThumbnail(resolveMediaUrl(imageUrl));
            } catch (imgErr) {
              console.log("excel thumbnail err:", imgErr);
            }
          }
          if (thumb) {
            const imageId = workbook.addImage({ buffer: thumb.buffer, extension: "jpeg" });
            const scale = THUMB_DISPLAY_PX / Math.max(thumb.width, thumb.height);
            embeddedImages.set(imageUrl, {
              imageId,
              width: Math.round(thumb.width * scale),
              height: Math.round(thumb.height * scale),
            });
          } else {
            failedThumbnails += 1;
          }
          setExportProgress((progress) => ({ ...progress, done: progress.done + 1 }));
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(THUMB_FETCH_CONCURRENCY, imageUrls.length) }, imageWorker)
      );

      groupedData.forEach((group) => {
        let firstRow = 0;
        // "Active" / "Deleted on dd/mm/yyyy" for rows tied to an image; "-" for
        // rows with none (credit adjustments, searches, uploads).
        const imageStatus = !group.has_image
          ? "-"
          : group.image_deleted
            ? `Deleted${group.image_deleted_at ? ` on ${formatDateIST(group.image_deleted_at)}` : ""}`
            : "Active";
        group.entries.forEach((entry, index) => {
          const excelRow = worksheet.addRow({
            // Image name/image/status are merged across the group below; only
            // the first line carries the value.
            image_name: index === 0 ? group.image_name : undefined,
            image_status: index === 0 ? imageStatus : undefined,
            date: group.date,
            requested_time: formatRequestedIST(entry),
            time: formatTimeIST(entry.transaction_on),
            service_type: entry.service_type,
            transaction_amount: entry.transaction_amount.toFixed(1),
            balance: entry.balance.toFixed(1),
          });
          excelRow.height = 28;
          if (index === 0) firstRow = excelRow.number;
        });
        const lastRow = firstRow + group.entries.length - 1;
        if (lastRow > firstRow) {
          worksheet.mergeCells(firstRow, 1, lastRow, 1);
          worksheet.mergeCells(firstRow, 2, lastRow, 2);
          worksheet.mergeCells(firstRow, 3, lastRow, 3);
        }
        worksheet.getCell(firstRow, 1).alignment = { vertical: "top" };
        worksheet.getCell(firstRow, 3).alignment = { vertical: "top" };

        const embedded = group.image_url ? embeddedImages.get(group.image_url) : null;
        if (embedded) {
          worksheet.addImage(embedded.imageId, {
            tl: { col: 1, row: firstRow - 1 },
            ext: { width: embedded.width, height: embedded.height },
          });
        }
      });

      const excelBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `Utilization_${fromDate}_to_${toDate}.xlsx`);

      if (failedThumbnails > 0) {
        toast({
          title: `Exported, but ${failedThumbnails} thumbnail(s) couldn't be embedded`,
          position: "top",
          status: "warning",
          duration: 4000,
          variant: "left-accent",
          isClosable: true,
        });
      }
    } catch (err) {
      console.log("export excel err:", err);
      toast({
        title: `Failed to export Excel !`,
        position: "top",
        status: "error",
        duration: 3000,
        variant: "left-accent",
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
      setExportProgress({ done: 0, total: 0 });
    }
  };

  const NoDataComponent = () => (
    <div style={{ textAlign: "center", padding: "20px" }}>
      <strong>No data found</strong>
    </div>
  );
  return (
    <div>
      <Flex
        padding={"0px 0px 3px 0px"}
        alignItems={"center"}
        justifyContent={"space-between"}
        borderBottom={"0.5px solid #BDBDBD"}
        mt={{ base: "8px", md: "12px", lg: "auto" }}
        mb={{ base: "6px", md: "9px", lg: "12px" }}
      >
        <h2>Utilization</h2>

        <NavLink to="/layout" className="crossBtn">
          <span><RxCross1/></span>
        </NavLink>
      </Flex>
      <Box>
        <form onSubmit={handleSubmit}>
          <Flex
            alignItems={"flex-start"}
            justifyContent={"flex-start"}
            gap={"10px"}
          >
            <FormControl>
              <FormLabel>From Date</FormLabel>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>To Date</FormLabel>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </FormControl>
            {(role === "superAdmin" || role === "Admin") && (
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Select
                  value={email || localStorage.getItem("email")}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                >
                  {users?.map((user) => (
                    <option key={user.email} value={user.email}>
                      {user.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
            {role === "user" && (
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input type="email" value={email} readOnly />
              </FormControl>
            )}
            <FormControl>
              <FormLabel>Utilization Type</FormLabel>
              <Select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Credit">Credit</option>
                <option value="Debit">Debit</option>
              </Select>
            </FormControl>
          </Flex>
          <Flex
            alignItems={"center"}
            justifyContent={"center"}
            gap={{ base: "10px", md: "15px" }}
            mt={"1.5rem"}
            mb={"1.5rem"}
          >
            <Button
              type="submit"
              variant={"outline"}
              colorScheme={"blue"}
              size={"sm"}
            >
              {isLoading ? "Loading..." : "Apply"}
            </Button>
            <Button
              variant={"outline"}
              colorScheme={"red"}
              size={"sm"}
            //   onClick={() => {
            //     setFromDate(firstDateOfMonth());
            //     setToDate(defaultToDate);
            //     setTransactionType("All");
            //     setTransactionData([]);
            //     const storedEmail = localStorage.getItem("email");
            //     setEmail(storedEmail);
            //     const trans_info = { email: storedEmail, from_date: firstDateOfMonth(), to_date: defaultToDate }
            //     getTransaction(trans_info)
            //   }}
			onClick={handleResetTransaction}
            >
              Reset
            </Button>
            <Button
              variant={"outline"}
              colorScheme={"green"}
              size={"sm"}
              leftIcon={<BsDownload />}
              onClick={handleExportExcel}
              isLoading={isExporting}
              loadingText={
                exportProgress.total > 0
                  ? `Images ${exportProgress.done}/${exportProgress.total}`
                  : "Exporting"
              }
              isDisabled={!(filteredData.length > 0)}
            >
              Download Excel
            </Button>
          </Flex>
        </form>
      </Box>
      {
        (isLoading) ? <Skeleton width={"100%"} height={"140px"}/> :
      <DataTable
        persistTableHead={true}
        columns={columns}
        data={groupedData}
        keyField="key"
        noDataComponent={<NoDataComponent />}
        customStyles={tableStyles}
        defaultSortFieldId="date"
        defaultSortAsc={false}
        pagination
        paginationPerPage={25}
        paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
      />
    }
    </div>
  );
};

export default Transaction;
